
import { useState, useEffect, useRef, useCallback } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Eye, EyeOff, Gift, AlertTriangle, Loader2, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { redirectAfterAuth, withReturnTo } from '../utils/returnTo';
import { APP_CONFIG } from '../config/appConfig';
import { authAPI } from '../services/api';
import ShopifyAuth from '../components/shared/ShopifyAuth';

const isShopifyBuild = APP_CONFIG.billingProvider === 'shopify';

const GSI_SRC = 'https://accounts.google.com/gsi/client';

// Module-level, cached, and NEVER removed.
//
// The old code appended the GSI script from a mount-only effect and removed it
// on cleanup. Now that initialisation is driven by the button node mounting
// (which can happen after an async trial-token validation), that pattern breaks
// two ways: a re-run would append a second <script>, and a cleanup could remove
// a script another render still needs. Caching the promise here means the tag
// is appended at most once per page load, and resolving straight away when
// `window.google` already exists makes repeat calls free.
let gsiScriptPromise: Promise<void> | null = null;

function loadGoogleIdentityScript(): Promise<void> {
    if (gsiScriptPromise) return gsiScriptPromise;
    gsiScriptPromise = new Promise<void>((resolve, reject) => {
        // Already loaded (e.g. Login.tsx got there first, or we came back to
        // this route) — nothing to fetch. Note that removing a <script> tag
        // does not undefine `window.google`, so this stays true even if
        // another component tears its own tag down.
        if (typeof window.google !== 'undefined') {
            resolve();
            return;
        }
        const script = document.createElement('script');
        script.src = GSI_SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => {
            // Clear the cache so a later mount can retry rather than being
            // stuck on a permanently-rejected promise.
            gsiScriptPromise = null;
            reject(new Error('Failed to load Google Identity Services'));
        };
        document.body.appendChild(script);
    });
    return gsiScriptPromise;
}

// Plan-TrialLinkInvalidTokenAndEnvHosts.md — FE UX gate for the `?trial=<token>`
// global admin signup link. The backend stays authoritative for the grant; this
// only decides whether to show the register UI (valid token) or an explicit
// invalid-link screen (invalid token), so a bad trial URL never silently drops
// someone into a locked, no-trial signup.
//   - 'none'       → no `?trial` param; render register exactly as before.
//   - 'validating' → validate call in flight; show a brief loading state.
//   - 'valid'      → render register + the 7-day trial banner.
//   - 'invalid'    → render the invalid-link screen; NO form / OAuth entry.
//   - 'error'      → validate call failed; degrade to plain signup (no banner).
type TrialTokenStatus = 'none' | 'validating' | 'valid' | 'invalid' | 'error';

const Register = () => {
    const { register, googleLogin } = useAuth();
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const navigate = useNavigate();
    const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
    // Plan-TrialSignupLink.md §H — a `?trial=<token>` param (from the global
    // admin signup link) grants a 7-day trial + Live Avatar. The backend is the
    // source of truth for the grant; we now ALSO validate it client-side purely
    // as a UX gate (Plan-TrialLinkInvalidTokenAndEnvHosts.md) so a bad token
    // shows an explicit invalid-link screen instead of a silent no-trial signup.
    const [searchParams] = useSearchParams();
    const trialToken = searchParams.get('trial') || undefined;
    const [trialStatus, setTrialStatus] = useState<TrialTokenStatus>(
        trialToken ? 'validating' : 'none'
    );

    // Validate the trial token (if present) before showing the register UI.
    useEffect(() => {
        if (!trialToken) {
            setTrialStatus('none');
            return;
        }
        let cancelled = false;
        setTrialStatus('validating');
        authAPI
            .validateTrialToken(trialToken)
            .then((res) => {
                if (cancelled) return;
                setTrialStatus(res.data?.valid ? 'valid' : 'invalid');
            })
            .catch(() => {
                // Backend always returns 200; a thrown error here means a
                // network/transport failure. Degrade to a plain signup (no
                // banner) rather than blocking a legitimate trial holder — the
                // backend still re-checks the token at register time.
                if (!cancelled) setTrialStatus('error');
            });
        return () => {
            cancelled = true;
        };
    }, [trialToken]);

    // The trial banner is now gated on a VALIDATED token, not mere param
    // presence, so an invalid/unverified token never shows the trial promise.
    const showTrialBanner = trialStatus === 'valid';

    // Google Sign-In initialisation is driven by the BUTTON NODE MOUNTING, not
    // by this component mounting.
    //
    // The old code ran `renderButton(document.getElementById(…))` from a
    // mount-only effect. With `?trial=<token>` the component returns the
    // "Checking your trial link…" spinner on first paint, so the button div is
    // not in the DOM yet: `getElementById` returned null, `renderButton(null)`
    // no-op'd, and an empty dep array meant nothing ever re-ran. Whether it
    // worked was a race between the (cacheable, cross-origin) GSI script and
    // the (uncacheable) validation call — so on repeat visits it failed every
    // time. A callback ref removes the race entirely: it cannot fire before the
    // node exists, and it always fires once it does.
    //
    // The `trialStatus === 'invalid'` branch never renders the div, so this
    // never runs there — which is the intended behaviour, not an oversight.

    // Latest-ref for the GSI callback. Google captures the `callback` we pass
    // to `initialize()` ONCE, so a plain closure would freeze whatever
    // `trialToken` was current at init time. Reading through a ref at click
    // time means the handler always sees today's value.
    const googleResponseRef = useRef<(response: any) => void>(() => { });

    const googleButtonRef = useCallback((node: HTMLDivElement | null) => {
        // React passes null on unmount; nothing to do.
        if (!node) return;
        if (isShopifyBuild) return; // Shopify build has no Google sign-in
        const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
        if (!clientId || clientId === 'your_google_client_id_here') return;

        loadGoogleIdentityScript()
            .then(() => {
                if (typeof window.google === 'undefined') return;
                window.google.accounts.id.initialize({
                    client_id: clientId,
                    callback: (response: any) => googleResponseRef.current(response),
                });
                window.google.accounts.id.renderButton(node, {
                    theme: 'outline',
                    size: 'large',
                    width: '100%',
                    text: 'continue_with',
                });
            })
            .catch(() => {
                // Script blocked or offline. Leave the slot empty rather than
                // surfacing an error — the email/password form still works.
            });
    }, []);

    const handleGoogleResponse = async (response: any) => {
        try {
            setLoading(true);
            setError('');
            const base64Url = response.credential.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
            const userData = JSON.parse(jsonPayload);

            await googleLogin({
                credential: response.credential,
                name: userData.name,
                email: userData.email,
                sub: userData.sub,
                picture: userData.picture,
                given_name: userData.given_name,
                family_name: userData.family_name,
                // Without this a Google signup from a `?trial=<token>` link
                // created an account with NO trial and no Live Avatar, with no
                // error shown — exactly the silent no-trial signup the
                // invalid-link screen above exists to prevent. The email path
                // in handleSubmit has always passed it; this one never did.
                // Sent raw, like the email path: the backend re-validates and
                // stays authoritative for the grant.
                trialToken,
            });
            redirectAfterAuth(navigate);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Google sign-in failed');
        } finally {
            setLoading(false);
        }
    };

    // Keep the ref pointing at the CURRENT handler after every render, so the
    // callback Google captured at init time never reads a stale `trialToken`.
    useEffect(() => {
        googleResponseRef.current = handleGoogleResponse;
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const fullName = `${firstName} ${lastName}`.trim();
            await register(email, password, fullName, companyName, recaptchaToken || undefined, {
                firstName,
                lastName,
                trialToken,
            });
            redirectAfterAuth(navigate);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Registration failed');
            recaptchaRef.current?.reset();
            setRecaptchaToken(null);
        } finally {
            setLoading(false);
        }
    };

    // The page's shared visual shell (background + centered glass card + logo)
    // so the loading / invalid-link screens match the register card exactly.
    const Shell = ({ children }: { children: React.ReactNode }) => (
        <div className="min-h-screen relative flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8 overflow-hidden">
            {/* Background Effects */}
            <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-bl from-purple-900/10 to-transparent pointer-events-none"></div>
            <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-purple-500/20 rounded-full blur-[100px] animate-pulse-glow pointer-events-none" />

            <div className="max-w-md w-full space-y-8 relative z-10 glass p-10 rounded-3xl shadow-2xl">
                <div className="text-center">
                    <a href="https://askprodigy.ai" rel="noopener" className="inline-flex items-center space-x-2 mb-6 group">
                        <img src="/logo-purple.png" alt="AskProdigy.ai" className="h-14 w-auto group-hover:scale-110 transition-transform dark:hidden" />
                        <img src="/logo-white.png" alt="AskProdigy.ai" className="h-14 w-auto group-hover:scale-110 transition-transform hidden dark:block" />
                    </a>
                    {children}
                </div>
            </div>
        </div>
    );

    // Trial token present, validation in flight — brief loading state.
    if (trialStatus === 'validating') {
        return (
            <Shell>
                <div className="flex flex-col items-center gap-3 py-4">
                    <Loader2 className="w-8 h-8 text-primary animate-spin" />
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Checking your trial link…
                    </p>
                </div>
            </Shell>
        );
    }

    // Trial token present but invalid/expired — explicit screen; we do NOT
    // render the register form or the Shopify OAuth entry, so a bad trial URL
    // can never silently create a locked, no-trial account.
    if (trialStatus === 'invalid') {
        return (
            <Shell>
                <div className="flex justify-center mb-4">
                    <div className="w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                        <AlertTriangle className="w-7 h-7 text-amber-600 dark:text-amber-400" />
                    </div>
                </div>
                <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white mb-2">
                    This trial link is invalid or expired
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                    We couldn't verify this trial sign-up link. It may have been
                    deactivated or the address was mistyped. Please double-check the
                    link, or ask whoever shared it for an up-to-date one.
                </p>

                <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 px-4 py-3 rounded-xl text-sm text-left mb-6">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>
                        These are standard sign-up pages — they do{' '}
                        <span className="font-semibold">not</span> include the free
                        trial. Signing up here will not start a trial.
                    </span>
                </div>

                <div className="flex flex-col gap-3">
                    <a
                        href={`${APP_CONFIG.appUrl}/register`}
                        className="w-full btn-primary flex items-center justify-center gap-2 py-3"
                    >
                        Go to app sign-up
                        <ArrowRight className="w-4 h-4" />
                    </a>
                    <a
                        href={`${APP_CONFIG.shopUrl}/register`}
                        className="w-full btn-secondary flex items-center justify-center gap-2 py-3"
                    >
                        Go to Shopify sign-up
                        <ArrowRight className="w-4 h-4" />
                    </a>
                </div>
            </Shell>
        );
    }

    return (
        <div className="min-h-screen relative flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8 overflow-hidden">
            {/* Background Effects */}
            <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-bl from-purple-900/10 to-transparent pointer-events-none"></div>
            <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-purple-500/20 rounded-full blur-[100px] animate-pulse-glow pointer-events-none" />

            <div className="max-w-md w-full space-y-8 relative z-10 glass p-10 rounded-3xl shadow-2xl">
                <div className="text-center">
                    <a href="https://askprodigy.ai" rel="noopener" className="inline-flex items-center space-x-2 mb-6 group">
                        <img src="/logo-purple.png" alt="AskProdigy.ai" className="h-14 w-auto group-hover:scale-110 transition-transform dark:hidden" />
                        <img src="/logo-white.png" alt="AskProdigy.ai" className="h-14 w-auto group-hover:scale-110 transition-transform hidden dark:block" />
                    </a>
                    <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-2">Create an account</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        {isShopifyBuild && showTrialBanner
                            ? 'Start your 7-day free trial with Shopify Sync today.'
                            : 'Start automating your customer support today.'}
                    </p>
                </div>

                {showTrialBanner && (
                    <div className="flex items-start gap-2 bg-primary/10 dark:bg-primary/20 border border-primary/30 text-primary dark:text-primary-200 px-4 py-3 rounded-xl text-sm">
                        <Gift className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>
                            <span className="font-semibold">7-day free trial + Live Avatar</span>.
                        </span>
                    </div>
                )}

                {isShopifyBuild ? (
                  <ShopifyAuth mode="signup" />
                ) : (
                <>
                {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-xl text-sm text-center">
                        {error}
                    </div>
                )}

                <form className="mt-8 space-y-4" onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">First name</label>
                            <input
                                type="text"
                                required
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                className="input-field"
                                placeholder="John"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Last name</label>
                            <input
                                type="text"
                                required
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                className="input-field"
                                placeholder="Doe"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Company</label>
                        <input
                            type="text"
                            required
                            value={companyName}
                            onChange={(e) => setCompanyName(e.target.value)}
                            className="input-field"
                            placeholder="Acme Inc."
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Email address</label>
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="input-field"
                            placeholder="you@company.com"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Password</label>
                        <div className="relative">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="input-field pr-11"
                                placeholder="••••••••"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(v => !v)}
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                            >
                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>

                    {recaptchaSiteKey && (
                        <div className="flex justify-center mt-2">
                            <ReCAPTCHA
                                ref={recaptchaRef}
                                sitekey={recaptchaSiteKey}
                                onChange={(token) => setRecaptchaToken(token)}
                                onExpired={() => setRecaptchaToken(null)}
                            />
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading || (!!recaptchaSiteKey && !recaptchaToken)}
                        className="w-full btn-primary flex justify-center py-3 mt-6"
                    >
                        {loading ? 'Creating account...' : 'Create Account'}
                    </button>

                    <div className="relative mt-6">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-300 dark:border-gray-700"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-2 bg-white dark:bg-gray-800 text-gray-500">Or continue with</span>
                        </div>
                    </div>

                    {/* ref (not getElementById) is what ties initialisation to
                        this node actually existing — see googleButtonRef. */}
                    <div ref={googleButtonRef} id="googleSignInButton" className="flex justify-center h-10 mt-4"></div>
                </form>

                <div className="text-center text-sm">
                    <p className="text-gray-600 dark:text-gray-400">
                        Already have an account?{' '}
                        <Link to={withReturnTo('/login')} className="font-medium text-primary hover:text-primary-600 transition-colors">
                            Sign in
                        </Link>
                    </p>
                </div>
                </>
                )}
            </div>
        </div>
    );
};

export default Register;
